import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useEditorState } from '@tiptap/react';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import { DEFAULT_FONT_SIZE, FONT_SIZES } from './documentEditorExtensions';

const BLOCK_STYLES = [
  { value: 'paragraph', label: 'Texto normal' },
  { value: 'h1', label: 'Título 1' },
  { value: 'h2', label: 'Título 2' },
  { value: 'h3', label: 'Título 3' },
];

const ALIGNMENTS = [
  { value: 'left', label: 'Alinhar à esquerda', Icon: FormatAlignLeftIcon },
  { value: 'center', label: 'Centralizar', Icon: FormatAlignCenterIcon },
  { value: 'right', label: 'Alinhar à direita', Icon: FormatAlignRightIcon },
  { value: 'justify', label: 'Justificar', Icon: FormatAlignJustifyIcon },
];

function ToolbarButton({ title, onClick, isActive, disabled, children }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          aria-label={title}
          aria-pressed={isActive}
          onClick={onClick}
          disabled={disabled}
          color={isActive ? 'primary' : 'default'}
          sx={{ borderRadius: 1, backgroundColor: isActive ? 'action.selected' : 'transparent' }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

ToolbarButton.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

ToolbarButton.defaultProps = {
  isActive: false,
  disabled: false,
};

export default function DocumentEditorToolbar({ editor, disabled, startActions }) {
  const [tableAnchorEl, setTableAnchorEl] = useState(null);

  // Re-renders the toolbar only when one of these selected values changes.
  const state = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      const headingLevel = [1, 2, 3].find((level) => currentEditor.isActive('heading', { level }));
      return {
        isBold: currentEditor.isActive('bold'),
        isItalic: currentEditor.isActive('italic'),
        isUnderline: currentEditor.isActive('underline'),
        isStrike: currentEditor.isActive('strike'),
        isBulletList: currentEditor.isActive('bulletList'),
        isOrderedList: currentEditor.isActive('orderedList'),
        isInTable: currentEditor.isActive('table'),
        blockStyle: headingLevel ? `h${headingLevel}` : 'paragraph',
        alignment:
          ALIGNMENTS.map((item) => item.value).find((value) =>
            currentEditor.isActive({ textAlign: value }),
          ) ?? 'left',
        fontSize: currentEditor.getAttributes('textStyle').fontSize ?? DEFAULT_FONT_SIZE,
        canUndo: currentEditor.can().undo(),
        canRedo: currentEditor.can().redo(),
      };
    },
  });

  const chain = () => editor.chain().focus();

  const changeBlockStyle = (value) => {
    if (value === 'paragraph') {
      chain().setParagraph().run();
      return;
    }
    chain()
      .toggleHeading({ level: Number(value.replace('h', '')) })
      .run();
  };

  const changeFontSize = (value) => {
    if (value === DEFAULT_FONT_SIZE) {
      chain().unsetFontSize().run();
      return;
    }
    chain().setFontSize(value).run();
  };

  const runTableCommand = (command) => {
    command(chain()).run();
    setTableAnchorEl(null);
  };

  const fontSizeOptions = FONT_SIZES.includes(state.fontSize)
    ? FONT_SIZES
    : [...FONT_SIZES, state.fontSize];

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 2,
      }}
    >
      {startActions && (
        <>
          {startActions}
          <Divider orientation="vertical" flexItem />
        </>
      )}

      <ToolbarButton
        title="Desfazer"
        onClick={() => chain().undo().run()}
        disabled={disabled || !state.canUndo}
      >
        <UndoIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Refazer"
        onClick={() => chain().redo().run()}
        disabled={disabled || !state.canRedo}
      >
        <RedoIcon fontSize="small" />
      </ToolbarButton>

      <Divider orientation="vertical" flexItem />

      <Select
        size="small"
        value={state.blockStyle}
        onChange={(event) => changeBlockStyle(event.target.value)}
        disabled={disabled}
        inputProps={{ 'aria-label': 'Estilo do texto' }}
        sx={{ minWidth: 130, fontSize: '0.85rem' }}
      >
        {BLOCK_STYLES.map((style) => (
          <MenuItem key={style.value} value={style.value}>
            {style.label}
          </MenuItem>
        ))}
      </Select>

      <Select
        size="small"
        value={state.fontSize}
        onChange={(event) => changeFontSize(event.target.value)}
        disabled={disabled}
        inputProps={{ 'aria-label': 'Tamanho da fonte' }}
        sx={{ minWidth: 80, fontSize: '0.85rem' }}
      >
        {fontSizeOptions.map((size) => (
          <MenuItem key={size} value={size}>
            {size.replace('pt', '')}
          </MenuItem>
        ))}
      </Select>

      <Divider orientation="vertical" flexItem />

      <ToolbarButton
        title="Negrito"
        onClick={() => chain().toggleBold().run()}
        isActive={state.isBold}
        disabled={disabled}
      >
        <FormatBoldIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Itálico"
        onClick={() => chain().toggleItalic().run()}
        isActive={state.isItalic}
        disabled={disabled}
      >
        <FormatItalicIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Sublinhado"
        onClick={() => chain().toggleUnderline().run()}
        isActive={state.isUnderline}
        disabled={disabled}
      >
        <FormatUnderlinedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Tachado"
        onClick={() => chain().toggleStrike().run()}
        isActive={state.isStrike}
        disabled={disabled}
      >
        <StrikethroughSIcon fontSize="small" />
      </ToolbarButton>

      <Divider orientation="vertical" flexItem />

      {ALIGNMENTS.map(({ value, label, Icon }) => (
        <ToolbarButton
          key={value}
          title={label}
          onClick={() => chain().setTextAlign(value).run()}
          isActive={state.alignment === value}
          disabled={disabled}
        >
          <Icon fontSize="small" />
        </ToolbarButton>
      ))}

      <Divider orientation="vertical" flexItem />

      <ToolbarButton
        title="Lista com marcadores"
        onClick={() => chain().toggleBulletList().run()}
        isActive={state.isBulletList}
        disabled={disabled}
      >
        <FormatListBulletedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Lista numerada"
        onClick={() => chain().toggleOrderedList().run()}
        isActive={state.isOrderedList}
        disabled={disabled}
      >
        <FormatListNumberedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Linha horizontal"
        onClick={() => chain().setHorizontalRule().run()}
        disabled={disabled}
      >
        <HorizontalRuleIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Tabela"
        onClick={(event) => setTableAnchorEl(event.currentTarget)}
        isActive={state.isInTable}
        disabled={disabled}
      >
        <TableChartOutlinedIcon fontSize="small" />
      </ToolbarButton>

      <Divider orientation="vertical" flexItem />

      <ToolbarButton
        title="Limpar formatação"
        onClick={() => chain().unsetAllMarks().clearNodes().run()}
        disabled={disabled}
      >
        <FormatClearIcon fontSize="small" />
      </ToolbarButton>

      <Menu
        anchorEl={tableAnchorEl}
        open={Boolean(tableAnchorEl)}
        onClose={() => setTableAnchorEl(null)}
      >
        <MenuItem
          onClick={() =>
            runTableCommand((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }))
          }
          disabled={state.isInTable}
        >
          <ListItemIcon>
            <TableChartOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Inserir tabela 3x3</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => runTableCommand((c) => c.addRowAfter())}
          disabled={!state.isInTable}
        >
          Adicionar linha abaixo
        </MenuItem>
        <MenuItem
          onClick={() => runTableCommand((c) => c.addColumnAfter())}
          disabled={!state.isInTable}
        >
          Adicionar coluna à direita
        </MenuItem>
        <MenuItem onClick={() => runTableCommand((c) => c.deleteRow())} disabled={!state.isInTable}>
          Excluir linha
        </MenuItem>
        <MenuItem
          onClick={() => runTableCommand((c) => c.deleteColumn())}
          disabled={!state.isInTable}
        >
          Excluir coluna
        </MenuItem>
        <MenuItem
          onClick={() => runTableCommand((c) => c.deleteTable())}
          disabled={!state.isInTable}
          sx={{ color: 'error.main' }}
        >
          Excluir tabela
        </MenuItem>
      </Menu>
    </Box>
  );
}

DocumentEditorToolbar.propTypes = {
  editor: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  startActions: PropTypes.node,
};

DocumentEditorToolbar.defaultProps = {
  disabled: false,
  startActions: null,
};
